// Catálogo de vistas del modo "Catálogo de muebles" (ADR-582).
//
// Archivo aparte de `vistasCatalogo.ts` a propósito: ese catálogo está escrito
// con vocabulario de moda (`calzado`, `torso`, `maniqui_frontal`, poses con
// modelo) y no queríamos ensuciarlo ni arriesgar el modo que está en producción.
// Se reusa el tipo `VistaCatalogo` para que el picker y la pantalla de review
// no necesiten un tipo paralelo.
//
// Las tres salen del deck "Demo Estudio Atlas — Sodimac", que propone tres
// templates por producto: Infografía, Foto en contexto y Video en contexto. El
// video queda fuera de la v1 (es otro flujo: Veo/Kling, créditos y tiempos
// distintos). El fondo blanco NO está en el deck como output —ahí es la foto de
// origen— pero se mantiene porque las fotos de e-commerce suelen venir sucias o
// mal recortadas y regenerar el packshot limpio tiene valor propio.

import type { VistaCatalogo } from './vistasCatalogo';

/** Slugs que viajan en `vista_producto` hacia n8n (`Generar anuncio V3`).
 *  Tienen que coincidir EXACTO con las keys del diccionario `vistasMuebles`
 *  del nodo code `vista prompts`. Si cambiás uno acá, cambialo allá. */
export const VISTA_MUEBLE_PACKSHOT = 'mueble_packshot';
export const VISTA_MUEBLE_INFOGRAFIA = 'mueble_infografia';
export const VISTA_MUEBLE_CONTEXTO = 'mueble_contexto';

export const VISTAS_MUEBLES: VistaCatalogo[] = [
  {
    id: VISTA_MUEBLE_PACKSHOT,
    nombre: 'Fondo blanco',
    imageUrl: '/images/vistas_muebles/packshot.png',
    kind: 'solo-producto',
    // Sin filtro por `model_view`: los productos de este rubro no pasan por el
    // categorizador de moda, así que su model_view es null y filtrar los
    // escondería a todos.
    compatibleModelViews: null,
  },
  {
    id: VISTA_MUEBLE_INFOGRAFIA,
    nombre: 'Infografía',
    imageUrl: '/images/vistas_muebles/infografia.png',
    kind: 'solo-producto',
    compatibleModelViews: null,
  },
  {
    id: VISTA_MUEBLE_CONTEXTO,
    nombre: 'Foto en contexto',
    imageUrl: '/images/vistas_muebles/contexto.png',
    kind: 'solo-producto',
    compatibleModelViews: null,
  },
];

/** Descripción corta que se muestra bajo el nombre en el picker. */
export const DESCRIPCION_VISTA_MUEBLE: Record<string, string> = {
  [VISTA_MUEBLE_PACKSHOT]: 'El producto solo, sobre fondo blanco de estudio.',
  [VISTA_MUEBLE_INFOGRAFIA]: 'Con las medidas y características marcadas.',
  [VISTA_MUEBLE_CONTEXTO]: 'El producto ambientado, a escala real.',
};

/**
 * Vistas que NO se pueden generar sin medidas cargadas.
 *
 * Solo la infografía: es literalmente una lámina de medidas, sin ellas no hay
 * nada que mostrar. Las otras dos se generan igual sin medidas — si están,
 * mejoran el prompt (evitan que la IA deforme el producto), pero no son
 * obligatorias. Ver ADR-584.
 */
export const VISTAS_QUE_EXIGEN_MEDIDAS = new Set<string>([
  VISTA_MUEBLE_INFOGRAFIA,
]);

export const esVistaMueble = (id: string): boolean =>
  VISTAS_MUEBLES.some((v) => v.id === id);

/**
 * Fondo que le corresponde a cada vista de muebles.
 *
 * En este modo NO hay selector global de Fondo (lo sacamos: cada vista ya
 * define el suyo, y un selector global se contradiría con la vista elegida).
 * Entonces el fondo se DERIVA de la vista, y esto es lo que decide por qué
 * rama del árbol de prompts de `Generar anuncio V3` cae cada una:
 *
 *   • packshot    → `blanco`  → rama "fondo blanco"  → prompt de producto
 *                                                       sobre ciclorama blanco
 *   • infografía  → `blanco`  → ídem (ver ⚠️ abajo)
 *   • contexto    → `ia`      → rama "con entorno"   → prompt de producto
 *                                                       integrado en una escena
 *
 * ⚠️ La infografía todavía NO tiene prompt propio en n8n. Mientras no lo tenga
 * va a caer en el mismo prompt que el packshot y va a salir como un packshot
 * común, sin cotas ni pills. No rompe nada; simplemente no es lo que promete
 * hasta que se agregue el nodo del lado de n8n. Ver ADR-584.
 */
export function backgroundParaVistaMueble(
  vistaId: string,
): Record<string, unknown> {
  if (vistaId === VISTA_MUEBLE_CONTEXTO) {
    // 'ia' = que el modelo invente la escena. Es lo que hace caer la
    // generación en la rama "con entorno" en vez de la de fondo blanco.
    return { background_mode: 'ia' };
  }
  // `background_id: 5` es el mismo que usa el default 'blanco' del Catálogo IA.
  return { background_mode: 'blanco', background_id: 5 };
}
